// Treemap de la map (d3) : meme logique de navigation que l'historique (racine -> groupe ->
// [serveur] -> environnement -> traitements), habillage moderne (cartes arrondies, palette de
// statuts, hachures + pulsation "en cours"). Au niveau environnement, les traitements sont
// regroupes en sous-cadres par categorie.
import * as d3 from "d3";
import React, { useEffect, useRef } from "react";

import { COULEURS_CATEGORIES, estKiwee, filenameJob } from "./categoriesFlux";
import { formaterDate, formaterDuree } from "./BarreFiltresMap";
import { BulleFlottante, ContenuBulleEnv, useBulle } from "./BulleFlottante";
import {
  COULEUR_EN_COURS,
  COULEURS_HISTORIQUE,
  STATUTS_META,
  STATUT_NON_REMONTE,
  agregerEnv,
  pireStatut,
  statutPrincipal,
  texteDernierBuild,
} from "./filtresMap";
import useResize from "../../hooks/useResize";

const COULEUR_TEXTE = "#1d2733";

// Coupe un texte pour tenir dans `largeur` pixels (largeur moyenne d'un caractere : `pas`).
export function tronquer(texte, largeur, pas = 6.5) {
  const t = String(texte === null || texte === undefined ? "" : texte);
  if (largeur < pas * 2) return "";
  const max = Math.floor(largeur / pas);
  return t.length <= max ? t : t.slice(0, Math.max(1, max - 1)) + "…";
}

const estEnvNoeud = (data) =>
  !!data && (data.type === "client" || (!data.type && Array.isArray(data.jobs)));

// Statut d'un noeud de donnees feuille (env ou traitement) : { statut, running }.
export function statutFeuille(data) {
  if (data && data.nonRemonte && estEnvNoeud(data)) return { statut: STATUT_NON_REMONTE, running: false };
  if (estEnvNoeud(data)) {
    const jobs = Array.isArray(data.jobs) ? data.jobs : [];
    return {
      statut: jobs.length ? pireStatut(jobs.map(statutPrincipal)) : "sans",
      running: jobs.some((j) => j && j.running),
    };
  }
  return { statut: statutPrincipal(data), running: !!(data && data.running) };
}

// Statut reel (status_id) d'un environnement expire : pastille sur la tuile grisee.
const STATUT_REEL = ["ok", "warning", "erreur", "grise"];

export default function VueTreemap({ data, path, onZoom, onJob, onEnvSans }) {
  const ref = useRef();
  const mapRef = useRef();
  const size = useResize(mapRef);
  const rappels = useRef({});
  rappels.current = { onZoom, onJob, onEnvSans };
  const { bulle, montrer, cacher } = useBulle();
  const montrerRef = useRef(montrer);
  const cacherRef = useRef(cacher);
  montrerRef.current = montrer;
  cacherRef.current = cacher;
  const niveau = path.length;

  useEffect(() => {
    if (!data || !data.children || !mapRef.current) return;
    const largeur = size?.width ?? mapRef.current.offsetWidth;
    const hauteur = size?.height ?? mapRef.current.offsetHeight;

    const racine = d3.hierarchy(data).sum((d) => (d.type === "client" || d.type === "job" ? 1 : 0));
    // statut / pulsation propages des feuilles vers les cadres
    racine.eachAfter((n) => {
      if (!n.children) {
        const s = n.data.type === "categorie" || n.data.type === "group" ? { statut: "sans", running: false } : statutFeuille(n.data);
        n.statut = s.statut;
        n.running = s.running;
      } else {
        n.statut = pireStatut(n.children.map((c) => c.statut));
        n.running = n.children.some((c) => c.running);
      }
    });
    // anomalies en haut a gauche, puis volume
    racine.sort(
      (a, b) =>
        STATUTS_META[b.statut].poids - STATUTS_META[a.statut].poids || b.value - a.value
    );
    d3
      .treemap()
      .size([largeur, hauteur])
      .round(true)
      .paddingInner(3)
      .paddingOuter((d) => (d.depth === 0 ? 0 : 4))
      .paddingTop((d) => (d.depth === 0 ? 0 : d.data.type === "server" ? 18 : 24))(racine);

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", [0, 0, largeur, hauteur]);
    // hachure fine des noeuds non remontes (BI-682, H5)
    svg
      .append("defs")
      .append("pattern")
      .attr("id", "map-hachures-nr")
      .attr("width", 5)
      .attr("height", 5)
      .attr("patternUnits", "userSpaceOnUse")
      .attr("patternTransform", "rotate(45)")
      .append("rect")
      .attr("width", 1.2)
      .attr("height", 5)
      .attr("fill", "rgba(255,255,255,.4)");
    svg
      .append("defs")
      .append("pattern")
      .attr("id", "map-hachures")
      .attr("width", 8)
      .attr("height", 8)
      .attr("patternUnits", "userSpaceOnUse")
      .attr("patternTransform", "rotate(45)")
      .append("rect")
      .attr("width", 3)
      .attr("height", 8)
      .attr("fill", "rgba(255,255,255,.35)");

    const noeuds = svg
      .selectAll("g.noeud")
      .data(racine.descendants().filter((d) => d.depth > 0))
      .join("g")
      .attr("class", "noeud")
      .attr("transform", (d) => `translate(${d.x0},${d.y0})`);

    noeuds.each(function (d) {
      const g = d3.select(this);
      const w = Math.max(0, d.x1 - d.x0);
      const h = Math.max(0, d.y1 - d.y0);
      const t = d.data.type;
      if (d.children || t === "group" || t === "server" || t === "categorie") {
        dessinerCadre(g, d, w, h, t, rappels);
      } else if (t === "job") {
        dessinerJob(g, d, w, h, rappels, montrerRef, cacherRef);
      } else {
        dessinerEnv(g, d, w, h, rappels, montrerRef, cacherRef);
      }
    });
    // niveau : le rendu depend du chemin (ex. env sous serveur) ; size : redimensionnement
  }, [data, niveau, size]);

  return (
    <div className="map-vue-treemap" ref={mapRef} data-testid="vue-treemap">
      <svg ref={ref} role="img" aria-label="Treemap des traitements" />
      <BulleFlottante bulle={bulle} />
    </div>
  );
}

/* ------------------------------- Dessin ------------------------------- */

function dessinerCadre(g, d, w, h, type, rappels) {
  const categorie = type === "categorie";
  const couleurCat = categorie ? COULEURS_CATEGORIES[d.data.categorie] || "#8c98a4" : null;
  g.append("rect")
    .attr("width", w)
    .attr("height", h)
    .attr("rx", type === "group" ? 10 : 8)
    .attr("fill", categorie ? `${couleurCat}12` : type === "group" ? "#f7f9fb" : "#ffffff")
    .attr("stroke", categorie ? `${couleurCat}66` : "#e4e8ee");
  const serveur = type === "server";
  g.append("rect")
    .attr("x", 6)
    .attr("y", serveur ? 5 : 8)
    .attr("width", serveur ? 6 : 8)
    .attr("height", serveur ? 6 : 8)
    .attr("rx", 4)
    .attr("fill", categorie ? couleurCat : STATUTS_META[d.statut].couleur);
  const erreurs = d.data.errors || d.data.errorCount || 0;
  const titre = categorie
    ? `${d.data.categorie} · ${d.value}`
    : `${d.data.name}${erreurs ? `  · ${erreurs} err.` : ""}`;
  g.append("text")
    .attr("x", serveur ? 16 : 20)
    .attr("y", serveur ? 13 : 17)
    .attr("font-size", serveur ? 10 : 12)
    .attr("font-weight", 600)
    .attr("fill", categorie ? couleurCat : COULEUR_TEXTE)
    .text(tronquer(titre, w - 26, serveur ? 5.8 : 7));
  if (!categorie) {
    g.attr("cursor", "pointer").on("click", (e) => rappels.current.onZoom(e, d));
  }
}

function dessinerEnv(g, d, w, h, rappels, montrerRef, cacherRef) {
  const data = d.data;
  const sansTraitement = !Array.isArray(data.jobs) || data.jobs.length === 0;
  const clair = d.statut === "sans";
  const texte = clair ? "#4b5866" : "#fff";
  g.append("rect")
    .attr("width", w)
    .attr("height", h)
    .attr("rx", 6)
    .attr("fill", STATUTS_META[d.statut].couleur)
    .attr("opacity", 0.92);
  if (d.running) {
    g.append("rect")
      .attr("class", "running")
      .attr("width", w)
      .attr("height", h)
      .attr("rx", 6)
      .attr("fill", "url(#map-hachures)");
  }
  if (data.nonRemonte) {
    g.append("rect")
      .attr("class", "non-remonte")
      .attr("width", w)
      .attr("height", h)
      .attr("rx", 6)
      .attr("fill", "url(#map-hachures-nr)");
  }
  const erreurs = Number(data.errors) || 0;
  const lignes = data.nonRemonte
    ? [data.name, "Non remonté", texteDernierBuild(data.dernierBuild, formaterDate)]
    : [data.name, data.vague_name];
  if (d.statut === "erreur" && erreurs) lignes.push(`${erreurs} err.`);
  if (data.latest_integrity === false) lignes.push("Erreur d'intégrité");
  // libelle des l'instant ou la tuile est lisible ; police reduite si petite
  const petit = w < 70 || h < 34;
  const police = petit ? 10 : 11;
  const pas = petit ? 5.6 : 6.3;
  const pasY = petit ? 11 : 14;
  const y0 = petit ? 11 : 16;
  lignes.forEach((l, i) => {
    const y = y0 + i * pasY;
    if (!l || y + 3 > h || w < 20 || h < 14) return;
    g.append("text")
      .attr("x", petit ? 3 : 6)
      .attr("y", y)
      .attr("font-size", police)
      .attr("font-weight", i === 0 ? 600 : 400)
      .attr("fill", texte)
      .attr("fill-opacity", i === 0 ? 1 : 0.85)
      .text(tronquer(l, w - (petit ? 5 : 12), pas));
  });
  // environnement expire : pastille du statut reel (si pas en cours)
  if (data.expired && !data.running && w > 40 && h > 24) {
    g.append("circle")
      .attr("cx", w - 12)
      .attr("cy", 12)
      .attr("r", 5)
      .attr("fill", STATUTS_META[STATUT_REEL[Number(data.status_id)] || "grise"].couleur)
      .attr("stroke", "#fff");
  }
  g.attr("cursor", "pointer")
    .on("click", (e) => {
      cacherRef.current();
      if (sansTraitement && !data.nonRemonte) rappels.current.onEnvSans(d);
      else rappels.current.onZoom(e, d);
    })
    .on("mousemove", (e) =>
      montrerRef.current(
        e,
        <ContenuBulleEnv
          env={agregerEnv(
            {
              group: data.group_name,
              server: data.server_name,
              env: data.name,
              code: data.code_client_cti,
              finess: data.finess_id,
              vague: data.vague_name,
              nonRemonte: !!data.nonRemonte,
              dernierBuild: data.dernierBuild,
            },
            data.jobs
          )}
        />
      )
    )
    .on("mouseleave", () => cacherRef.current());
}

function dessinerJob(g, d, w, h, rappels, montrerRef, cacherRef) {
  const data = d.data;
  g.append("rect")
    .attr("width", w)
    .attr("height", h)
    .attr("rx", 6)
    .attr("fill", d.running ? COULEUR_EN_COURS : STATUTS_META[d.statut].couleur)
    .attr("opacity", 0.92);
  if (d.running) {
    g.append("rect")
      .attr("class", "running")
      .attr("width", w)
      .attr("height", h)
      .attr("rx", 6)
      .attr("fill", "url(#map-hachures)");
  }
  [data.name, formaterDate(data.start_date_time), formaterDuree(data.duration)].forEach((l, i) => {
    const y = 16 + i * 14;
    if (!l || y + 4 > h || w < 26) return;
    g.append("text")
      .attr("x", 6)
      .attr("y", y)
      .attr("font-size", 11)
      .attr("font-weight", i === 0 ? 600 : 400)
      .attr("fill", "#fff")
      .attr("fill-opacity", i === 0 ? 1 : 0.85)
      .text(tronquer(l, w - 12, 6.3));
  });

  // 3 pastilles d'historique cliquables (derniers builds)
  const historique = Array.isArray(data.history) ? data.history : [];
  if (h >= 78 && w >= 76) {
    [0, 1, 2].forEach((i) => {
      const entree = Array.isArray(historique[i]) ? historique[i] : null;
      const c = g
        .append("circle")
        .attr("cx", 14 + i * 24)
        .attr("cy", 62)
        .attr("r", 8)
        .attr("fill", COULEURS_HISTORIQUE[Number(entree && entree[0])] || COULEURS_HISTORIQUE[3])
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5);
      if (entree && entree[1]) {
        c.attr("cursor", "pointer").on("click", (e) => rappels.current.onJob(e, d, i));
      }
      c.on("mousemove", (e) => {
        e.stopPropagation();
        montrerRef.current(
          e,
          entree ? (
            <div>
              <div>Le {formaterDate(entree[2]) || "—"}</div>
              <div>Durée : {formaterDuree(entree[3]) || "—"}</div>
            </div>
          ) : (
            <div>Pas d&apos;exécution</div>
          )
        );
      }).on("mouseleave", () => cacherRef.current());
    });
  }
  // badge Kiwee (la categorie n'est pas modifiee)
  if (estKiwee(filenameJob(data)) && h >= 98 && w >= 56) {
    g.append("rect")
      .attr("x", 6)
      .attr("y", 78)
      .attr("width", 46)
      .attr("height", 14)
      .attr("rx", 7)
      .attr("fill", "#6d28d9");
    g.append("text")
      .attr("x", 13)
      .attr("y", 88)
      .attr("font-size", 10)
      .attr("fill", "#fff")
      .text("KIWEE");
  }
  g.attr("cursor", "pointer")
    .on("click", (e) => rappels.current.onJob(e, d, null))
    .on("mousemove", (e) =>
      montrerRef.current(
        e,
        <div>
          <b>{data.name}</b>
          <br />
          {STATUTS_META[d.statut].libelle}
          {d.running ? " · en cours" : ""}
        </div>
      )
    )
    .on("mouseleave", () => cacherRef.current());
}
